/**
 * NOTE: This file is part of the legacy Notes feature.
 * User-facing text has been renamed to "Documentation" for clarity.
 * For the new Notes feature, check the Hub implementation.
 */

import { Input } from "@/components/ui/input.tsx";
import { PinIcon, SearchIcon } from "lucide-react";
import { NoteTimelineItem } from "@/components/conversation-details/notes/note-timeline-item.tsx";
import React from "react";
import { Note, useCreateNote } from "@/lib/api/notes.ts";
import { NoteSchema } from "@/lib/hooks/conversations/use-note-form.tsx";
import { useNoteUtils } from "@/lib/hooks/conversations/use-note-utils.ts";
import { NotesWithCallLogs } from "@/components/conversation-details/notes/notes-with-call-logs";
import { NoteRenderer } from "@/components/conversation-details/notes/note-renderer";
import { CallTimelineItem } from "@/components/conversation-details/call-timeline-item.tsx";
import { useAuth } from "../../auth/auth-context";
import { NotesList } from "./notes-list";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { useAbility } from "@/lib/casl/ability-context";

interface NotesProps {
  notes: Note[];
  mentorshipId?: string;
  adminMode?: boolean;
  menteeId: string;
  mentorId: string;
}

export function Notes({
  notes,
  mentorshipId,
  adminMode,
  menteeId,
  mentorId,
}: NotesProps) {
  const { mutateAsync } = useCreateNote();
  const [search, setSearch] = React.useState("");
  const { track } = useTrackEvent();
  const ability = useAbility();
  const canCreate = ability.can("create", "documentation");
  const canUpdate = ability.can("update", "documentation");

  const { user } = useAuth();
  const { convertFormValuesToReminder } = useNoteUtils();

  const addNote = async (payload: NoteSchema) => {
    if (!mentorshipId || !canCreate) return;

    const reminder = convertFormValuesToReminder(
      payload.date,
      payload.time,
      payload.timezone,
    );

    mutateAsync({
      title: payload.title || new Date().toLocaleString(),
      content: payload.content,
      mentorshipId,
      reminder,
      metadata: {
        reminderTimezone: payload.timezone,
      },
    });

    track(PostHogEvents.DOCUMENT_CREATED, {
      mentorship_id: mentorshipId,
      has_reminder: !!reminder,
    });
  };

  const filteredNotes = search?.length
    ? notes.filter(
        (note) =>
          note.title.toLowerCase().includes(search.toLowerCase()) ||
          note.content.toLowerCase().includes(search.toLowerCase()),
      )
    : notes;

  const pinnedNotes =
    notes?.filter((note) => note?.metadata?.pinnedByMentor) || [];

  return (
    <div className="my-4 pb-20">
      {!!pinnedNotes?.length && (
        <div className="mb-6 px-4">
          <div className="inline-flex items-center justify-start gap-2">
            <PinIcon size={16} className="text-zinc-400" />
            <div className="text-base font-normal text-zinc-400">Pinned</div>
          </div>

          <NotesList
            notes={pinnedNotes}
            menteeId={menteeId}
            pinnedNotes
            mentorId={mentorId}
            adminMode={adminMode}
          />
        </div>
      )}
      <Input
        placeholder="Search"
        startIcon={
          <SearchIcon size={16} className="ml-1 text-muted-foreground" />
        }
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="mt-6">
        <h2 className="text-2xl font-bold leading-relaxed text-black">
          Documentation
        </h2>

        <div className="mt-4">
          {canCreate && (
            <NoteTimelineItem
              isNew
              heading="Create Documentation"
              headingType="new"
              noteActions={{
                handleSave: addNote,
              }}
            />
          )}

          <NotesWithCallLogs
            mentorshipId={mentorshipId}
            notes={filteredNotes}
            renderNote={(note) => {
              const isNoteReadOnly =
                !canUpdate ||
                note.createdBy?.id !== user?.id ||
                note?.metadata?.type === "escalation";

              return (
                <NoteRenderer
                  note={note}
                  pinnedNotes={false}
                  readonlyMode={isNoteReadOnly}
                  adminMode={adminMode}
                  menteeId={menteeId}
                  mentorId={mentorId}
                />
              );
            }}
            renderCallLog={(call) => {
              return <CallTimelineItem key={call.id} call={call} />;
            }}
          />
        </div>
      </div>
    </div>
  );
}
